// src/store/transito/actions.ts
import { Commit } from "vuex";
import { Axios } from "axios";
import { TransitoFilters, TransitoRow, TransitoState } from ".";

type DestroyTransitoPayload =
  | { item: number }
  | { items: number[] }
  | { id: number; cerrar: true };

function cleanParams(obj: any) {
  const out: any = {};
  Object.keys(obj || {}).forEach((k) => {
    const v = (obj as any)[k];
    if (v !== null && v !== undefined && String(v).trim() !== "") out[k] = v;
  });
  return out;
}

export default {
  setSelectedFilters(
    { commit }: { commit: Commit },
    selectedFilters: TransitoFilters
  ) {
    commit("SET_SELECTED_FILTERS", selectedFilters);
  },

  async getTransito(
    { commit, state }: { commit: Commit; state: TransitoState },
    {
      axios,
      apiRoute,
      url = null,
      searchParams = {},
    }: {
      axios: Axios;
      apiRoute: string;
      url: string | null;
      searchParams: any;
    }
  ) {
    const token = localStorage.getItem("authToken");
    commit("setLoading", true);
    commit("setErrorMessage", null);

    const finalUrl = url ?? `${apiRoute}/transito/show`;

    // mezcla filtros guardados + lo que venga en la llamada
    const params = cleanParams({
      ...state.selectedFilters,
      ...searchParams,
    });

    try {
      const response = await axios.get(finalUrl, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // tu backend devuelve { success:true, data: paginator }
      const pag = response.data.data;

      commit("setNextPageUrl", pag.next_page_url);
      commit("setPrevPageUrl", pag.prev_page_url);
      commit("setFirstPageUrl", pag.first_page_url);
      commit("setLastPageUrl", pag.last_page_url);

      commit("setLastPage", pag.last_page);
      commit("setCurrentPage", pag.current_page);
      commit("setTotal", pag.total);
      commit("setFrom", pag.from);
      commit("setTo", pag.to);

      commit("setTransitoData", pag.data);
    } catch (err: any) {
      commit(
        "setErrorMessage",
        err?.response?.data?.message ||
        "Ocurrió un error consultando tránsito. Intenta nuevamente."
      );
      console.error(err);
    } finally {
      commit("setLoading", false);
      commit("setIsLoadingMore", false);
    }
  },

  async showOneTransito(
    { commit }: { commit: Commit },
    {
      axios,
      apiRoute,
      item,
    }: { axios: Axios; apiRoute: string; item: number }
  ): Promise<TransitoRow | null> {
    const token = localStorage.getItem("authToken");
    commit("setErrorMessage", null);

    try {
      const response = await axios.get(`${apiRoute}/transito/showOne`, {
        params: { item },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data as TransitoRow;
    } catch (err: any) {
      commit(
        "setErrorMessage",
        err?.response?.data?.message || "No se pudo cargar el registro."
      );
      console.error(err);
      return null;
    }
  },

  async createTransito(
    { commit }: { commit: Commit },
    {
      axios,
      apiRoute,
      payload,
    }: { axios: Axios; apiRoute: string; payload: Partial<TransitoRow> }
  ) {
    const token = localStorage.getItem("authToken");
    commit("setErrorMessage", null);

    const response = await axios.post(`${apiRoute}/transito/store`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    commit("upsertTransito", response.data.data);
    return response.data.data as TransitoRow;
  },

  async updateTransito(
    { commit }: { commit: Commit },
    {
      axios,
      apiRoute,
      payload,
    }: {
      axios: Axios;
      apiRoute: string;
      payload: Partial<TransitoRow> & { item: number };
    }
  ) {
    const token = localStorage.getItem("authToken");
    commit("setErrorMessage", null);

    const response = await axios.put(`${apiRoute}/transito/update`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    commit("upsertTransito", response.data.data);
    return response.data.data as TransitoRow;
  },

  async deleteTransito(
    { commit }: { commit: Commit },
    {
      axios,
      apiRoute,
      payload,
    }: {
      axios: Axios;
      apiRoute: string;
      payload: DestroyTransitoPayload;
    }
  ) {
    const token = localStorage.getItem("authToken");
    commit("setErrorMessage", null);

    try {
      // 1) Cerrar pedido completo: ?id=55&cerrar=1
      if ("id" in payload && payload.cerrar === true) {
        await axios.delete(`${apiRoute}/transito/destroy`, {
          params: { id: payload.id, cerrar: 1 },
          headers: { Authorization: `Bearer ${token}` },
        });

        commit("deleteTransitoPedido", payload.id);
        return;
      }

      // 2) Eliminar por arreglo de items (JSON body): { items: [1,2,3] }
      if ("items" in payload) {
        const items = Array.from(new Set(payload.items))
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n) && n > 0);

        if (items.length === 0) {
          commit("setErrorMessage", "items inválidos");
          return;
        }

        await axios.delete(`${apiRoute}/transito/destroy`, {
          data: { items },
          headers: { Authorization: `Bearer ${token}` },
        });

        commit("deleteTransitoItems", items);
        return;
      }

      // 3) Eliminar un solo item: ?item=123
      if ("item" in payload) {
        await axios.delete(`${apiRoute}/transito/destroy`, {
          params: { item: payload.item },
          headers: { Authorization: `Bearer ${token}` },
        });

        commit("deleteTransito", payload.item);
      }
    } catch (err: any) {
      commit(
        "setErrorMessage",
        err?.response?.data?.message || "No se pudo eliminar el/los registro(s)."
      );
      console.error(err);
      throw err;
    }
  },
};